import React, { useEffect, useState } from 'react';
import { useHistory, Prompt } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Col, FormGroup, Label, Button, Input, Row } from 'reactstrap';
import { BASE_URL, API, ESTADOS_OPORTUNIDAD } from 'CONST';
import { patch, post } from 'helpers/requests';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useExitPrompt from 'components/shared/useExitPrompt';
import NewFileTable from '../shared/NewFileTable';
import CheckModal from '../shared/CheckModal';
import WarningModal from '../UI/WarningModal';
import AvanzarOportunidadConfirmationModal from './AvanzarOportunidadConfirmationModal';

const CargarContrafianzaSection = ({ fullData, id, estadosOportunidad, observaciones }) => {
  const requisitosEmision = {
    contrafianza_firmada_ante_notario: {
      label: 'Contrafianza Firmada Ante Notario',
      adjunto_docs: 'contrafianza_firmada_ante_notario_docs',
    },
  };

  const emptyAdjuntos = {
    contrafianza_firmada_ante_notario: [],
  };

  const emptyAdjuntosDocs = {
    contrafianza_firmada_ante_notario_docs: [],
  };

  const [newAntecedentes, setNewAntecedentes] = useState(emptyAdjuntos);
  const [oldAntecedentes, setOldAntecedentes] = useState(emptyAdjuntosDocs);
  const [adjuntosEliminados, setAdjuntosEliminados] = useState([]);

  const history = useHistory();
  const [observacion, setObservacion] = useState('');
  const [observacionesEstado, setObservacionesEstado] = useState([]);
  const [editableSection, setEditableSection] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [isFormSaved, setIsFormSaved] = useState(true);

  const editableStates = [ESTADOS_OPORTUNIDAD.exposicionMaximaHabilitada];

  const getBodyDocs = () => {
    return {
      contrafianza_firmada_ante_notario: newAntecedentes.contrafianza_firmada_ante_notario.map(
        (d) => d.uuid,
      ),
    };
  };

  useEffect(() => {
    if (fullData && fullData.antecedentes) {
      setOldAntecedentes(fullData.antecedentes);
    }
    if (fullData && fullData.estado && editableStates.includes(fullData.estado.nemotecnico)) {
      setEditableSection(true);
    }
  }, [fullData]);

  useEffect(() => {
    if (observaciones) {
      setObservacionesEstado(
        observaciones.filter(
          (obs) => obs.estado.nemotecnico === ESTADOS_OPORTUNIDAD.contrafianzaCargada,
        ),
      );
    }
  }, [observaciones, fullData]);

  const deleteAdjunto = () => {
    patch(
      `${BASE_URL}${API.antecedentes}${oldAntecedentes.id}/borrar-adjunto/`,
      adjuntosEliminados,
    ).then((response) => {
      setOldAntecedentes(response);
      setAdjuntosEliminados([]);
    });
  };

  const removeAdjunto = (idDoc, tipoDoc) => {
    setAdjuntosEliminados((prevState) => [...prevState, [idDoc, tipoDoc]]);
  };

  useExitPrompt((e) => {
    e.preventDefault();
    e.returnValue = '';
  }, !isFormSaved);

  const postObservacion = () => {
    if (observacion) {
      const bodyObservacion = {
        oportunidad_id: id,
        estado_id: estadosOportunidad.find(
          (e) => e.nemotecnico === ESTADOS_OPORTUNIDAD.contrafianzaCargada,
        ).id,
        observacion,
      };
      post(`${BASE_URL}${API.observacionesOportunidad}`, bodyObservacion)
        .then(() => {
          setShowUpdateModal(true);
        })
        .catch(() => {
          setShowErrorModal(true);
        });
    }
  };

  const avanzarOportunidad = () => {
    const bodyDocs = getBodyDocs();
    setLoading(true);
    patch(`${BASE_URL}${API.antecedentes}${oldAntecedentes.id}/`, bodyDocs)
      .then((response) => {
        setOldAntecedentes(response);
        setNewAntecedentes(emptyAdjuntos);
        setIsFormSaved(true);
        const body = {
          estado_id: estadosOportunidad.find(
            (e) => e.nemotecnico === ESTADOS_OPORTUNIDAD.contrafianzaCargada,
          ).id,
        };
        if (adjuntosEliminados.length) deleteAdjunto();
        patch(`${BASE_URL}${API.oportunidadesLinea}${id}/`, body)
          .then(() => {
            postObservacion();
            setShowUpdateModal(true);
          })
          .catch(() => {
            setShowErrorModal(true);
          });
      })
      .catch(() => {
        setShowErrorModal(true);
      })
      .finally(() => {
        setConfirmationModal(false);
        setLoading(false);
      });
  };

  return (
    <div>
      <FormGroup>
        {Object.keys(requisitosEmision).map((key) => {
          return (
            <div key={key}>
              <Row>
                <Col sm={2}>
                  <Label>
                    {requisitosEmision[key].label}
                    <span className="required text-danger">*</span>
                  </Label>
                </Col>
                <Col>
                  <NewFileTable
                    handleAdd={(file) => {
                      setIsFormSaved(false);
                      setNewAntecedentes((prevState) => ({
                        ...prevState,
                        [key]: [...prevState[key], file],
                      }));
                    }}
                    handleDeleteNew={(file) => {
                      setIsFormSaved(false);
                      setNewAntecedentes((prevState) => ({
                        ...prevState,
                        [key]: prevState[key].filter((doc) => doc.uuid !== file.uuid),
                      }));
                    }}
                    handleDelete={(event) => {
                      setIsFormSaved(false);
                      removeAdjunto(event.id, key);
                      setOldAntecedentes((prevState) => ({
                        ...prevState,
                        [requisitosEmision[key].adjunto_docs]: prevState[
                          requisitosEmision[key].adjunto_docs
                        ].filter((doc) => doc.id !== event.id),
                      }));
                    }}
                    uploadUrl={`${BASE_URL}${API.upload}/`}
                    downloadUrl={`${BASE_URL}${API.download}/`}
                    newFiles={newAntecedentes[key]}
                    fileList={oldAntecedentes[requisitosEmision[key].adjunto_docs]}
                    individualMaxFileSize={13}
                    globalMaxFileSize={20}
                    loginIdToken=""
                    readOnly={!editableSection}
                  />
                </Col>
              </Row>
              <br />
            </div>
          );
        })}
      </FormGroup>
      <FormGroup row>
        <Col sm={2}>
          <Label>Observaciones</Label>
        </Col>
        <Col>
          <Input
            id="observacion-info-inicial"
            type="textarea"
            value={
              !editableSection && observacionesEstado.length > 0
                ? observacionesEstado[0].observacion
                : observacion || ''
            }
            onChange={(event) => setObservacion(event.target.value)}
            rows="3"
            disabled={!editableSection}
          />
        </Col>
      </FormGroup>
      {observacionesEstado.slice(!editableSection).map((obs) => {
        return (
          <FormGroup row key={obs.id}>
            <Col sm={2} />
            <Col>
              <Input type="textarea" value={obs.observacion} rows="3" disabled />
            </Col>
          </FormGroup>
        );
      })}
      {editableSection && (
        <div className="button-container d-flex justify-content-end">
          <Button
            className="btn btn-secondary blue"
            onClick={() => setConfirmationModal(true)}
            disabled={
              !newAntecedentes.contrafianza_firmada_ante_notario.length || confirmationModal
            }
          >
            {loading ? <FontAwesomeIcon spin={loading} icon={faSpinner} /> : 'Cargar Contrafianza'}
          </Button>
        </div>
      )}
      <AvanzarOportunidadConfirmationModal
        openModal={confirmationModal}
        setOpenModal={setConfirmationModal}
        avanzarOportunidad={avanzarOportunidad}
        mensajeModal="¿Está seguro que desea CARGAR LA CONTRAFIANZA?"
        loading={loading}
      />
      <CheckModal
        isOpen={showUpdateModal}
        toggle={() => history.push('/garantia/oportunidades')}
        handleOk={() => history.push('/garantia/oportunidades')}
        okBtnText="OK"
        confirmMsg="Contrafianza cargada exitosamente."
      />
      <WarningModal
        isOpen={showErrorModal}
        toggle={() => setShowErrorModal(false)}
        handleClearError={() => setShowErrorModal(false)}
        errorMsg="Ha ocurrido un error al cargar la contrafianza."
        loading={false}
        onlyCancelButton
      />
      <Prompt
        when={!isFormSaved}
        message="¿Está seguro que desea salir? Es posible que los cambios no se guarden."
      />
    </div>
  );
};

CargarContrafianzaSection.propTypes = {
  id: PropTypes.string,
  fullData: PropTypes.shape({
    antecedentes: PropTypes.shape({
      id: PropTypes.number,
    }),
    afianzado: PropTypes.shape({
      id: PropTypes.number,
    }),
    estado: PropTypes.shape({
      id: PropTypes.number,
      nemotecnico: PropTypes.string,
    }),
    id: PropTypes.number,
    monto: PropTypes.number,
    exposicion_maxima: PropTypes.number,
  }),
  estadosOportunidad: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      nemotecnico: PropTypes.string,
    }),
  ),
  observaciones: PropTypes.arrayOf(
    PropTypes.shape({
      estado: PropTypes.shape({
        id: PropTypes.number,
        nemotecnico: PropTypes.string,
      }),
      id: PropTypes.number,
    }),
  ),
};

CargarContrafianzaSection.defaultProps = {
  fullData: null,
  id: null,
  estadosOportunidad: [],
  observaciones: [],
};

export default CargarContrafianzaSection;
